import React, { useEffect, useState } from "react";
import moment from "moment";
import { formatIsoToLocal } from "features/linehaul-outbound-planning/utils/relativeTimeUtils";
import "./timestampDisplay.scss";

export type TimestampDisplayFormat = "relative" | "local";

export interface TimestampDisplayProps {
  /** ISO-8601 timestamp string */
  value?: string | null;
  /** Primary display format */
  format?: TimestampDisplayFormat;
  /** Show detailed tooltip on hover (local time + raw ISO) */
  showTooltip?: boolean;
  emptyPlaceholder?: string;
  className?: string;
}

const RELATIVE_TICK_MS = 30_000;

const TimestampDisplay: React.FC<TimestampDisplayProps> = ({
  value,
  format = "relative",
  showTooltip = true,
  emptyPlaceholder = "—",
  className,
}) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!value || format !== "relative") return;
    const id = window.setInterval(() => setTick((n) => n + 1), RELATIVE_TICK_MS);
    return () => clearInterval(id);
  }, [value, format]);

  if (!value) {
    return <span className={className}>{emptyPlaceholder}</span>;
  }

  const m = moment(value);
  if (!m.isValid()) {
    return <span className={className}>{emptyPlaceholder}</span>;
  }

  const primaryText =
    format === "relative" ? m.fromNow() : formatIsoToLocal(value) || m.format("MM/DD/YY h:mm A");

  const localLabel = formatIsoToLocal(value) || m.local().format("MM/DD/YY h:mm A");
  const rawIso = m.toISOString();
  const title = showTooltip
    ? `Local: ${localLabel}\nRaw: ${rawIso}`
    : undefined;

  return (
    <span className={`xgs-timestamp-display${className ? ` ${className}` : ""}`} title={title}>
      {primaryText}
    </span>
  );
};

export default TimestampDisplay;
